// chooseStore

import m from "mithril"
import { MithrilTsxComponent } from "mithril-tsx-component"
import { ICoords, IStore } from "./utils"
import * as Api from "./api"
import { getCulture, getTranslation } from "./translations"
import { Svg } from "../../svg"
import { cleanString, debounce } from "oj-utils"

declare var google: any

export interface IStoreSearch { onchange: (stores: IStore[]) => void, ongeo: (geo: ICoords) => void }
export class StoreSearch extends MithrilTsxComponent<IStoreSearch> {
  searchVal = ""
  geoError: string
  suggestions: google.maps.places.PlacePrediction[] = []
  open: boolean = false

  view(v: m.Vnode<IStoreSearch>) {
    return <div className={cleanString("store-search-container", this.open && "open")}>
      <div className="store-search-bar">
        <div className="input-container">
          <input
            className="input pac-target-input"
            name="shop-search"
            aria-label={getTranslation("searchPlaceholder")}
            placeholder={getTranslation("searchPlaceholder")}
            value={this.searchVal}
            onfocus={() => {
              this.openSearch(v, true)
              if (this.searchVal) {
                this.makeAutocompleteRequest()
              }
            }}
            oninput={async (e) => {
              this.searchVal = e.target.value
              this.makeAutocompleteRequest()
            }}
            onkeydown={(e) => {
              if (e.code === "Enter" && this.suggestions.length > 0) {
                this.findStoresBySuggestion(v, this.suggestions[0])
                this.openSearch(v, false)
              }
            }} />
          {this.searchVal?.length > 0 && <button className="clear" aria-label={getTranslation("clear")} onclick={() => {
            this.searchVal = ""
            this.openSearch(v, false)
          }}>
            <Svg src="/FrontendWebpack/dist/assets/newIcons/clear.svg" />
          </button>}
          <button className="search-icon" onclick={() => {
            this.openSearch(v, true)
            this.makeAutocompleteRequest()
          }} aria-label={getTranslation("searchPlaceholder")}>
            <Svg src="/FrontendWebpack/dist/assets/newIcons/search.svg" />
          </button>
        </div>
        <button type="button" className="link-geo" onclick={e => this.searchByGeo(v)}>
          <span>{getTranslation("geo")}</span>
          <i className="icon"><Svg src="/FrontendWebpack/dist/assets/newIcons/pin.svg" /></i>
        </button>
        {this.geoError &&
          <span>{this.geoError}</span>
        }
      </div>
      {this.suggestions.length > 0 && <div className="store-search-suggestions" inert={!this.open}>
        {this.suggestions.map(suggestion =>
          <div className="store-search-suggestion">
            <button onclick={() => {
              this.searchVal = suggestion.text.toString()
              this.openSearch(v, false)
              this.findStoresBySuggestion(v, suggestion)
            }}>
              <span>{suggestion.text.toString()}</span>
            </button>
          </div>)}
      </div>}
    </div>
  }

  makeAutocompleteRequest = debounce(250, async () => {

    if (!this.searchVal) {
      this.suggestions = [];
      m.redraw()
      return;
    }

    const { AutocompleteSuggestion } = (await google.maps.importLibrary(
      'places'
    )) as google.maps.PlacesLibrary;

    const request: google.maps.places.AutocompleteRequest = {
      input: this.searchVal,
      language: getCulture(),
      includedRegionCodes: getCulture().includes("be") ? ["be", "lu"] : ["nl"],
    }
    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

    this.suggestions = suggestions.map(suggestion => suggestion.placePrediction);
    m.redraw()
  })

  async searchByGeo(v: m.Vnode<IStoreSearch>) {
    try {
      const { latitude, longitude } = await new Promise<{ latitude: number, longitude: number }>((resolve, reject) => {
        if (navigator.geolocation)
          navigator.geolocation.getCurrentPosition(position => resolve(position.coords))
        else
          reject(getTranslation("geoNotSupported"))
      })

      const geo = { lat: latitude, lng: longitude }

      this.findStoresByCoords(v, geo)
      v.attrs.ongeo(geo)
    }
    catch (err) {
      this.geoError = err.message
    }
  }

  async findStoresBySuggestion(v: m.Vnode<IStoreSearch>, suggestion: google.maps.places.PlacePrediction) {
    const place = suggestion.toPlace()
    if (!place) return
    await place.fetchFields({ fields: ["location"] })

    this.findStoresByCoords(v, { lat: place.location.lat(), lng: place.location.lng() })
  }

  async findStoresByCoords(v: m.Vnode<IStoreSearch>, coords: ICoords) {
    let eid = new URL(location.href).searchParams.get('eid')

    if (eid)
      eid = encodeURIComponent(eid)

    await Api.findStoresByCoords(coords, eid as string).then(stores => {
      v.attrs.onchange(stores)
    })
    m.redraw()
  }

  openSearch(v, open: boolean) {
    this.open = open
    if (this.open) {
      this.clickOutside(v)
      this.focusOutside(v)
    } else {
      this.suggestions = []
    }
  }

  focusOutside(v) {
    document.on("focusin.search", (event: FocusEvent) => {
      if (this.open && !v.dom.contains(document.activeElement)) {
        document.off("click.search")
        document.off("focusin.search")
        this.open = false
        this.suggestions = []
        document.body.classList.remove("search-active")
        m.redraw()
      }
    });
  }

  clickOutside(v) {
    if (!this.open)
      return

    document.on("click.search", (e) => {
      const isClickInside = v.dom.contains(e.target)

      if (isClickInside)
        return

      document.off("click.search")
      document.off("focusin.search")
      this.open = false
      this.suggestions = []
      document.body.classList.remove("search-active")
      m.redraw()
    })
  }
}
